import type {ImageSource} from 'UI/components/modules/ResponsiveImage/ResponsiveImage';

import type {Media} from 'types/payload-types';

/** Result of building responsive image data from a Payload Media object. */
export interface ResponsiveImageData {
    /** Alt text. */
    alt: string;
    /** Focal point X (0–100). */
    focalX?: number | null;
    /** Focal point Y (0–100). */
    focalY?: number | null;
    /** Best single URL for the given context. */
    src: string;
    /** Array of sources for srcset. */
    srcSet: ImageSource[];
}

const HERO_WIDTH = 1200;
const HERO_LARGE_WIDTH = 1920;

/** Size names ordered by width ascending. */
const SIZE_ORDER: {key: keyof NonNullable<Media['sizes']>; width: number}[] = [
    {
        key: 'thumbnail',
        width: 300
    },
    {
        key: 'card',
        width: 600
    },
    {
        key: 'tablet',
        width: 900
    },
    {
        key: 'hero',
        width: HERO_WIDTH
    },
    {
        key: 'heroLarge',
        width: HERO_LARGE_WIDTH
    }
];

/**
 * Builds responsive image data (srcSet, focal point, best src) from a Payload Media object.
 * Picks the best single `src` based on the preferred minimum width.
 *
 * @param media          The Payload Media document (populated).
 * @param preferredWidth Minimum preferred width for the fallback `src`. Defaults to 1200.
 * @returns The responsive image data, or `null` if no valid URL is available.
 */
export function buildResponsiveImageData(
    media: Media | null | undefined, preferredWidth = HERO_WIDTH
): ResponsiveImageData | null {
    if (!media) return null;

    const srcSet: ImageSource[] = [];

    for (const {key, width} of SIZE_ORDER) {
        const size = media.sizes?.[key];

        if (size?.url) {
            srcSet.push({
                url: size.url,
                width: size.width ?? width
            });
        }
    }

    // Add original as largest source if available and larger than heroLarge
    if (media.url && media.width && media.width > HERO_LARGE_WIDTH) {
        srcSet.push({
            url: media.url,
            width: media.width
        });
    }

    // Pick the best src: smallest size >= preferredWidth, fallback to largest available
    let src = media.url ?? '';

    for (const {key, width} of SIZE_ORDER) {
        const size = media.sizes?.[key];

        if (size?.url && width >= preferredWidth) {
            src = size.url;
            break;
        }
    }

    // If no size met the preferredWidth, use the largest available
    if (!src) {
        for (let i = SIZE_ORDER.length - 1; i >= 0; i--) {
            const size = media.sizes?.[SIZE_ORDER[i].key];

            if (size?.url) {
                src = size.url;
                break;
            }
        }
    }

    if (!src) return null;

    return {
        alt: media.alt ?? '',
        focalX: media.focalX,
        focalY: media.focalY,
        src,
        srcSet
    };
}